/**
 * COMMAND QUEUE V1 — the decision-focused home page (pilot-gated at '/').
 *
 * One composer call (GET /api/command-queue) feeds the whole page. Layout per
 * the approved sketch: masthead + one pulse line, then three lanes —
 *   · Needs you  — ranked decision cards (the card contract lives server-side;
 *                  this page just renders it: category chip + why-now, one
 *                  plain sentence, evidence line linking to the owning screen,
 *                  1–3 link actions with the FIRST recommended)
 *   · Watching   — rising signal clusters, not yet decisions
 *   · Handled    — muted rows of what automation already did (last 24h)
 *
 * The page performs NO mutations except snooze ("Not now", 3 days) and
 * dismiss — allowed only on money/estimate cards, never on blocking cards.
 * Every card action navigates to the existing screen that owns the work.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { PageSpinner } from "@/components/ui/spinner";
import { useFeatureSpotlight } from "@/components/feature-spotlight";
import { cn } from "@/lib/utils";
import { AlertTriangle, Eye, CheckCircle2, Sparkles, X, Zap } from "lucide-react";

export type CardCategory = "payroll" | "money" | "deadline" | "system" | "field";

export interface CommandCardAction {
    label: string;
    kind: "link";
    href: string;
}

export interface CommandCard {
    id: string;
    category: CardCategory;
    whyNow: string;
    what: string;
    detail: string | null;
    evidenceUrl: string;
    actions: CommandCardAction[];
    score: number;
    urgent: boolean;
}

export interface WatchingItem {
    id: string;
    kind: string;
    label: string;
    category: string;
    count: number;
    distinctUsers: number;
    lastSeen: string;
    href: string;
}

export interface HandledItem {
    id: string;
    at: string;
    action: string;
    sentence: string;
}

export interface CommandQueueResponse {
    pulse: {
        needsYou: number;
        watching: number;
        handledOvernight: number;
        readyForDispatch: number;
        inField: number;
        inReview: number;
    };
    needsYou: CommandCard[];
    watching: WatchingItem[];
    handled: HandledItem[];
}

const CATEGORY_LABEL: Record<CardCategory, string> = {
    payroll: "Payroll",
    money: "Money",
    deadline: "Deadline",
    system: "System",
    field: "Field",
};

const CATEGORY_VARIANT: Record<CardCategory, BadgeProps["variant"]> = {
    payroll: "destructive",
    money: "success",
    deadline: "warning",
    system: "muted",
    field: "default",
};

/** Snooze/dismiss only where the decision can wait: money/estimate cards.
 *  Never on urgent (payroll-/tech-blocking) cards. */
export function canSnooze(card: CommandCard): boolean {
    return !card.urgent && card.category !== "payroll";
}

export function canDismiss(card: CommandCard): boolean {
    return !card.urgent && card.category === "money";
}

export function relativeTime(iso: string): string {
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
}

function QueueCard({
    card,
    onSnooze,
    onDismiss,
}: {
    card: CommandCard;
    onSnooze: (id: string) => void;
    onDismiss: (id: string) => void;
}) {
    return (
        <Card
            data-testid="cq-card"
            className={cn("relative", card.urgent && "border-destructive/60 ring-1 ring-destructive/30")}
        >
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={CATEGORY_VARIANT[card.category]}>{CATEGORY_LABEL[card.category]}</Badge>
                        <span className="text-xs text-muted-foreground">
                            {card.urgent && (
                                <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-destructive" aria-hidden />
                            )}
                            {card.whyNow}
                        </span>
                    </div>
                    {canDismiss(card) && (
                        <button
                            type="button"
                            aria-label="Dismiss"
                            className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                            onClick={() => onDismiss(card.id)}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <p className="text-sm font-semibold leading-snug">{card.what}</p>
            </CardHeader>
            <CardContent>
                {card.detail && (
                    <Link
                        to={card.evidenceUrl}
                        className="block text-xs text-muted-foreground underline-offset-2 hover:underline"
                    >
                        {card.detail}
                    </Link>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    {card.actions.slice(0, 3).map((action, i) => (
                        <Button
                            key={action.href + action.label}
                            size="sm"
                            variant={i === 0 ? "default" : "outline"}
                            asChild
                        >
                            <Link to={action.href}>{action.label}</Link>
                        </Button>
                    ))}
                    {canSnooze(card) && (
                        <Button size="sm" variant="ghost" onClick={() => onSnooze(card.id)}>
                            Not now
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * First-open banner for the rebuilt City Filing Queue (feature_announcements
 * row seeded by migration 399). Renders at the top of the Command Center
 * until the user acknowledges — either by opening the walkthrough (reading
 * it counts as seeing it) or via the X.
 */
function FilingWalkthroughBanner() {
    const navigate = useNavigate();
    const { announcement, acknowledge } = useFeatureSpotlight("city_filing_walkthrough_jul17");
    if (!announcement) return null;

    return (
        <Card data-testid="filing-walkthrough-banner" className="border-primary/40">
            <CardContent className="flex items-start gap-3 pt-4">
                <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-snug">{announcement.headline}</p>
                    <p className="mt-1 text-xs text-muted-foreground leading-snug">
                        {announcement.body ?? "One page walks you through the whole queue, step by step."}
                    </p>
                    <Button
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                            acknowledge();
                            navigate("/help/filing-walkthrough");
                        }}
                    >
                        Read the walkthrough
                    </Button>
                </div>
                <button
                    type="button"
                    aria-label="Dismiss announcement"
                    className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                    onClick={acknowledge}
                >
                    <X className="h-4 w-4" />
                </button>
            </CardContent>
        </Card>
    );
}

export default function CommandPage() {
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useQuery({
        queryKey: ["command-queue"],
        queryFn: () => api.get<CommandQueueResponse>("/api/command-queue"),
    });

    const snooze = useMutation({
        mutationFn: (cardId: string) =>
            api.post(`/api/command-queue/cards/${encodeURIComponent(cardId)}/snooze`, { days: 3 }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["command-queue"] }),
    });
    const dismiss = useMutation({
        mutationFn: (cardId: string) => api.post(`/api/command-queue/cards/${encodeURIComponent(cardId)}/dismiss`, {}),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["command-queue"] }),
    });

    if (isLoading) return <PageSpinner />;
    if (isError || !data) {
        return (
            <div className="space-y-4">
                <PageHeader title="Command" />
                <Card>
                    <CardContent className="pt-5 text-sm text-muted-foreground">
                        Couldn&apos;t load the command queue. Refresh, or use the classic dashboard from the nav.
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { pulse, needsYou, watching, handled } = data;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Command"
                description={
                    // Pulse line under the masthead — one line, counts only.
                    <span data-testid="cq-pulse">
                        <strong>{pulse.needsYou}</strong> need you · <strong>{pulse.watching}</strong> watching ·{" "}
                        <strong>{pulse.handledOvernight}</strong> handled overnight
                        <span className="mx-2 opacity-50">|</span>
                        Ready for dispatch <strong>{pulse.readyForDispatch}</strong> · In the field{" "}
                        <strong>{pulse.inField}</strong> · In review <strong>{pulse.inReview}</strong>
                    </span>
                }
            />

            <FilingWalkthroughBanner />

            <div className="grid gap-6 lg:grid-cols-[1.7fr_1fr]">
                {/* ── Lane 1: Needs you ─────────────────────────────────── */}
                <section aria-label="Needs you" className="space-y-3">
                    <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-shell-foreground/90">
                        <Zap className="h-4 w-4" aria-hidden /> Needs you
                    </h2>
                    {needsYou.length === 0 ? (
                        <Card>
                            <CardContent className="pt-5 text-sm text-muted-foreground">
                                Nothing needs a decision right now. Nice.
                            </CardContent>
                        </Card>
                    ) : (
                        needsYou.map((card) => (
                            <QueueCard
                                key={card.id}
                                card={card}
                                onSnooze={(id) => snooze.mutate(id)}
                                onDismiss={(id) => dismiss.mutate(id)}
                            />
                        ))
                    )}
                </section>

                <div className="space-y-6">
                    {/* ── Lane 2: Watching ──────────────────────────────── */}
                    <section aria-label="Watching" className="space-y-3">
                        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-shell-foreground/90">
                            <Eye className="h-4 w-4" aria-hidden /> Watching
                        </h2>
                        <Card>
                            <CardContent className="divide-y divide-border/60 pt-4">
                                {watching.length === 0 ? (
                                    <p className="pb-1 text-sm text-muted-foreground">Nothing rising right now.</p>
                                ) : (
                                    watching.map((w) => (
                                        <Link
                                            key={w.id}
                                            to={w.href}
                                            className="block py-2 first:pt-0 last:pb-0 hover:bg-secondary/40"
                                            data-testid="cq-watching-row"
                                        >
                                            <p className="text-sm font-medium leading-snug">{w.label}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {w.count} reports · {w.distinctUsers} people · last{" "}
                                                {relativeTime(w.lastSeen)}
                                            </p>
                                        </Link>
                                    ))
                                )}
                            </CardContent>
                        </Card>
                    </section>

                    {/* ── Lane 3: Handled (muted) ───────────────────────── */}
                    <section aria-label="Handled" className="space-y-3">
                        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-shell-foreground/90">
                            <CheckCircle2 className="h-4 w-4" aria-hidden /> Handled
                        </h2>
                        <Card className="opacity-80">
                            <CardContent className="divide-y divide-border/60 pt-4">
                                {handled.length === 0 ? (
                                    <p className="pb-1 text-sm text-muted-foreground">
                                        No automated actions in the last 24 hours.
                                    </p>
                                ) : (
                                    handled.map((h) => (
                                        <div
                                            key={h.id}
                                            className="py-2 first:pt-0 last:pb-0"
                                            data-testid="cq-handled-row"
                                        >
                                            <p className="text-sm text-muted-foreground">{h.sentence}</p>
                                            <p className="text-xs text-muted-foreground/70">{relativeTime(h.at)}</p>
                                        </div>
                                    ))
                                )}
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </div>
        </div>
    );
}
